'use client'

import { ReactNode, RefObject, TouchEventHandler, createRef, useRef, useState } from 'react'

/**
 * Please refer to DAC7 (vinted.fr/dac7) and Item Verification Landing page (vinted.fr/item_verification) to see the use case and implementation.
 */

type SlidersWrapperProps<T = unknown> = {
  childData: Array<T>
  childComponent: React.FC<T>
  nextButton: ReactNode
  previousButton: ReactNode
}

const minSwipeDistance = 50
const sliderSpeed = 0.5

const SlidersWrapper = <T,>({
  childData,
  childComponent,
  nextButton,
  previousButton,
}: SlidersWrapperProps<T>) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const wrapperDivRef = useRef<HTMLDivElement>(null)
  const touchStart = useRef<number | null>(null)
  const touchEnd = useRef<number | null>(null)

  const Component = childComponent

  const itemrefs = useRef<Array<RefObject<HTMLDivElement | null>>>(childData.map(() => createRef()))

  // TODO: simplify this logic
  const validateNextIndex = index => (index === childData.length - 1 ? 0 : index + 1)
  const validatePreviousIndex = index => (index === 0 ? childData.length - 1 : index - 1)
  const goToNextItem = () => {
    const nextIndex = validateNextIndex(currentCardIndex)
    const sliderItemRef = itemrefs.current[nextIndex]!
    const wrapperRef = wrapperDivRef.current

    if (sliderItemRef.current && wrapperRef) {
      if (
        Math.round(wrapperRef.scrollLeft) >=
        Math.round(wrapperRef.scrollWidth - wrapperRef.clientWidth)
      ) {
        wrapperRef.style.cssText = `
          transform: translateX(0px);
          transition: all ${sliderSpeed}s;
        `
        setCurrentCardIndex(0)
      } else {
        wrapperRef.style.cssText = `
          transform: translateX(${-sliderItemRef.current.offsetWidth * nextIndex}px);
          transition: all ${sliderSpeed}s;
        `
        setCurrentCardIndex(prevCard => validateNextIndex(prevCard))
      }
    }
  }
  const goToPreviousItem = () => {
    const nextIndex = validatePreviousIndex(currentCardIndex)
    const sliderItemRef = itemrefs.current[nextIndex]!
    const wrapperRef = wrapperDivRef.current

    if (sliderItemRef.current && wrapperRef) {
      if (nextIndex === childData.length - 1) {
        wrapperRef.style.cssText = `
          transform: translateX(${-sliderItemRef.current.offsetWidth * nextIndex}px);
          transition: all ${sliderSpeed}s;
        `
        setCurrentCardIndex(nextIndex)
      } else {
        wrapperRef.style.cssText = `
          transform: translateX(${-sliderItemRef.current.offsetWidth * nextIndex}px);
          transition: all ${sliderSpeed}s;
        `
        setCurrentCardIndex(prevCard => validatePreviousIndex(prevCard))
      }
    }
  }

  const handleOnTouchStart: TouchEventHandler<HTMLDivElement> = element => {
    touchEnd.current = null
    touchStart.current = element.targetTouches[0]!.clientX
  }

  const handleOnTouchMove: TouchEventHandler<HTMLDivElement> = element => {
    touchEnd.current = element.targetTouches[0]!.clientX
  }

  const handleOnTouchEnd: TouchEventHandler<HTMLDivElement> = () => {
    if (!touchStart.current || !touchEnd.current) return
    const distance = touchStart.current - touchEnd.current
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      goToNextItem()
    }

    if (isRightSwipe) {
      goToPreviousItem()
    }
  }

  const renderItems = () =>
    childData.map((props, index) => {
      return (
        <div ref={itemrefs.current[index]} className="slider-wrapper-item" key={index}>
          <Component key={index} {...props} />
        </div>
      )
    })

  return (
    <div>
      <div
        className="slider-wrapper-items-container"
        ref={wrapperDivRef}
        onTouchMove={handleOnTouchMove}
        onTouchStart={handleOnTouchStart}
        onTouchEnd={handleOnTouchEnd}
      >
        {renderItems()}
      </div>
      {(previousButton || nextButton) && (
        <div className="slider-wrapper-buttons-container">
          <button
            type="button"
            className="slider-wrapper-button"
            onClick={goToPreviousItem}
            data-testid="slider-previous"
          >
            {previousButton}
          </button>
          <button
            type="button"
            className="slider-wrapper-button"
            onClick={goToNextItem}
            data-testid="slider-next"
          >
            {nextButton}
          </button>
        </div>
      )}
    </div>
  )
}

export default SlidersWrapper
